/**
 * HM-423 cluster 21 Clienti CRM: drawer dettaglio cliente con storico
 * soggiorni, VIP toggle, edit note interne.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getGuestDetail, patchGuest } from '../../api/guests-crm.js';

function formatItalianDateTime(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

export function GuestDetailDrawer({
  guestId,
  onClose,
  onVipToggle,
}: {
  guestId: string;
  onClose: () => void;
  onVipToggle: (isVip: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['guests', 'detail', guestId],
    queryFn: () => getGuestDetail(guestId),
  });

  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  useEffect(() => {
    if (detailQuery.data && notesDraft === null) {
      setNotesDraft(detailQuery.data.guest.internalNotes ?? '');
    }
  }, [detailQuery.data, notesDraft]);

  const notesMutation = useMutation({
    mutationFn: (notes: string) => patchGuest(guestId, { internalNotes: notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests', 'detail', guestId] });
      queryClient.invalidateQueries({ queryKey: ['guests', 'list'] });
    },
  });

  const data = detailQuery.data;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      // biome-ignore lint/a11y/useSemanticElements: pattern overlay div coerente con BookingDetailDrawer Planning (cluster 17); <dialog> nativo richiede show()/close() API non adottata in v2.6.
      role="dialog"
      aria-modal="true"
      data-testid="guest-detail-drawer"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.3)',
        zIndex: 100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        style={{
          width: 480,
          maxWidth: '90vw',
          background: '#fff',
          height: '100%',
          overflowY: 'auto',
          padding: 24,
          boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#3D2F24' }}>
            {data ? `${data.guest.lastName} ${data.guest.firstName}` : 'Caricamento…'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            data-testid="guest-drawer-close"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        {detailQuery.isLoading && <p>Caricamento…</p>}
        {detailQuery.error && (
          <p style={{ color: '#B85450' }}>Errore: {(detailQuery.error as Error).message}</p>
        )}

        {data && (
          <>
            <div style={{ marginTop: 16, fontSize: 14, color: '#3D2F24' }}>
              <div>📞 {data.guest.phone ?? '—'}</div>
              <div>✉ {data.guest.email ?? '—'}</div>
              {data.guest.documentType && (
                <div>
                  📄 {data.guest.documentType} {data.guest.documentNumber}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => onVipToggle(!data.guest.isVip)}
                  data-testid="guest-drawer-vip-toggle"
                  style={{
                    background: data.guest.isVip ? '#E97B3D' : '#fff',
                    color: data.guest.isVip ? '#fff' : '#E97B3D',
                    border: '1px solid #E97B3D',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {data.guest.isVip ? '★ Cliente VIP' : '☆ Imposta VIP'}
                </button>
              </div>
            </div>

            <section style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 13, color: '#7A6957', textTransform: 'uppercase' }}>
                Riepilogo
              </h3>
              <div style={{ fontSize: 14 }}>
                Soggiorni: <strong>{data.stats.staysCount}</strong>
                <br />
                Totale speso: <strong>€ {data.stats.totalSpent}</strong>
                <br />
                Primo soggiorno:{' '}
                {data.stats.firstStayAt ? formatItalianDateTime(data.stats.firstStayAt) : '—'}
                <br />
                Ultimo soggiorno:{' '}
                {data.stats.lastStayAt ? formatItalianDateTime(data.stats.lastStayAt) : '—'}
              </div>
            </section>

            <section style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 13, color: '#7A6957', textTransform: 'uppercase' }}>
                Note interne hotel
              </h3>
              <textarea
                value={notesDraft ?? ''}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={3}
                data-testid="guest-drawer-notes-input"
                placeholder="Note visibili solo allo staff (es. preferenze, problemi pregressi)…"
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 6,
                  border: '1px solid #D4C4B0',
                  fontSize: 13,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              <button
                type="button"
                onClick={() => notesMutation.mutate(notesDraft ?? '')}
                disabled={notesMutation.isPending || notesDraft === data.guest.internalNotes}
                data-testid="guest-drawer-notes-save"
                style={{
                  marginTop: 6,
                  padding: '6px 12px',
                  background: '#3D2F24',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {notesMutation.isPending ? 'Salvo…' : 'Salva note'}
              </button>
            </section>

            <section style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 13, color: '#7A6957', textTransform: 'uppercase' }}>
                Storico soggiorni ({data.bookings.length})
              </h3>
              {data.bookings.length === 0 ? (
                <p style={{ color: '#7A6957', fontSize: 13 }}>Nessun soggiorno registrato.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {data.bookings.map((b) => (
                    <li
                      key={b.id}
                      style={{
                        padding: 10,
                        marginBottom: 6,
                        background: '#FAF7F2',
                        borderRadius: 6,
                        fontSize: 13,
                      }}
                      data-testid={`guest-drawer-booking-${b.id}`}
                    >
                      <div>
                        <strong>{b.publicCode}</strong> ·{' '}
                        {b.service === 'day_use' ? 'Day Use' : 'Pernotto'} ·{' '}
                        <span
                          style={{
                            padding: '1px 6px',
                            borderRadius: 3,
                            background: '#fff',
                            fontSize: 11,
                          }}
                        >
                          {b.status}
                        </span>
                      </div>
                      <div style={{ color: '#7A6957', fontSize: 12 }}>
                        {formatItalianDateTime(b.startAt)} → {formatItalianDateTime(b.endAt)}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        € {Number(b.totalAmount).toFixed(2)}
                        {Number(b.extraAmount) > 0 &&
                          ` + extra € ${Number(b.extraAmount).toFixed(2)}`}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
